import React, { useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Box,
  Tooltip,
  Typography,
  useTheme,
  useMediaQuery
} from '@mui/material';

import {
  Close as CloseIcon,
  Home as HomeIcon,
  Info as InfoIcon,
  Computer as ComputerIcon,
  Code as CodeIcon,
  Mail as MailIcon,
  Menu as MenuIcon,
  Devices as DevicesIcon,
  Checklist as ChecklistIcon,
  Storage as StorageIcon,
  Business as BusinessIcon,
  PeopleAlt as CareerIcon
} from '@mui/icons-material';

//import WorkOutlineIcon from "@mui/icons-material/PeopleAlt";

import { SiteConfig, sites } from '../siteConfig';
import Chat from './itsupport/chat';

interface SidebarMenuProps {
  open: boolean;
  onClose: () => void;
}

export default function SidebarMenu({ open, onClose }: SidebarMenuProps) {
  const location = useLocation();
  const path = location.pathname.split('/')[1] as keyof typeof sites;
  const siteConfig: SiteConfig = sites[path] || sites.itsupport; // default site
  const isActivePath = (linkPath: string) => location.pathname === linkPath;
  const isWorkflowSite = path === 'workflow';

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const effectiveOpen = isMobile ? false : open;

  return (
    <Drawer
      anchor="left"
      variant="permanent"
      open={effectiveOpen}
      sx={{
        width: isMobile ? 56 : (open ? 240 : 56),
        flexShrink: 0,

        '& .MuiDrawer-paper': {
          height: `calc(100vh - ${siteConfig.headerHeight || 64}px)`,
          marginTop: `${siteConfig.headerHeight || 64}px`,
          width: isMobile ? 56 : (open ? 240 : 56),
          boxSizing: 'border-box',
          backgroundColor: siteConfig.sidebarColor || siteConfig.primaryColor,
          color: 'black',
          transition: 'width 0.3s ease-in-out',
          overflowX: 'hidden',

          // On mobile, disable hover effects and keep it always at 56px
          ...(isMobile && {
            width: '56px !important',
            minWidth: '56px !important',
            maxWidth: '56px !important',

          }),
        },
      }}
    >
      {/* Header / Toggle */}
      <Box
        sx={{
          p: effectiveOpen ? 2 : 1,
          display: 'flex',
          justifyContent: effectiveOpen ? 'space-between' : 'center',
          alignItems: 'center',

          // Hide toggle button on mobile
          ...(isMobile && {
            display: 'none',
          })
        }}
      >

        {effectiveOpen ? (
          <>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              Menu
            </Typography>
            <IconButton onClick={onClose} sx={{ color: 'black' }} size="small">
              <CloseIcon />
            </IconButton>
          </>
        ) : (
          <Tooltip title="Expand Menu" placement="right" arrow>
            <IconButton onClick={onClose} sx={{ color: 'black' }} size="small">
              <MenuIcon />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Menu Items */}
      <List sx={{ p: 0 }}>
        {siteConfig.navLinks.map((link) => {
          const linkPath = link.isTopLevel ? `/${link.href}` : `/${path}/${link.href}`;
          return (
            <Tooltip
              key={link.label}
              title={!open ? link.label : ''}
              placement="right"
              arrow
            >
              <ListItem
                component={RouterLink}
                to={linkPath}
                sx={{
                  py: 1.5,
                  px: open ? 2 : 1,
                  justifyContent: open ? 'flex-start' : 'center',
                  backgroundColor: isActivePath(linkPath) ? '#e0f2fe' : 'transparent',
                  borderLeft: isActivePath(linkPath)
                    ? '4px solid #3b82f6'
                    : '4px solid transparent',
                  '&:hover': {
                    backgroundColor: '#f1f5f9',
                    borderLeft: open ? '4px solid #94a3b8' : '4px solid transparent',
                  },
                  transition: 'all 0.2s ease-in-out',
                  minHeight: 48,
                  display: 'flex',
                  alignItems: 'center',
                  color: isActivePath(linkPath) ? '#3b82f6' : 'inherit',
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: open ? 2 : 0,
                    justifyContent: 'center',
                    color: isActivePath(linkPath) ? '#3b82f6' : 'inherit',
                  }}
                >
                  {link.icon === 'Home' ? <HomeIcon /> :
                    link.icon === 'Info' ? <InfoIcon /> :
                      link.icon === 'Computer' ? <ComputerIcon /> :
                        link.icon === 'Code' ? <CodeIcon /> :
                          link.icon === 'Mail' ? <MailIcon /> :
                            link.icon === 'Devices' ? <DevicesIcon /> :
                              link.icon === 'Checklist' ? <ChecklistIcon /> :
                                link.icon === 'Career' ? <CareerIcon /> :
                                  link.icon === 'Storage' ? <StorageIcon /> :
                                    link.icon === 'Calendar' ? <BusinessIcon /> :
                                      link.icon === 'Business' ? <BusinessIcon /> : null}
                </ListItemIcon>
                <ListItemText primary={open ? link.label : ''} />
              </ListItem>
            </Tooltip>
          );
        })}
      </List>
      {/* Conditionally render Chat for non-workflow sites */}
      {!isWorkflowSite && <Chat />}

    </Drawer>
  );
}
